import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { apiRequest } from "../helpers/api";
import { PreferencesSkeleton } from "../components/skeleton/PageSkeletons";
import { ToastFeedback } from "../components/ToastNotifications";
import "./Notifications.css";

const PAGE_SIZE = 20;
const filters = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" }
];

const relativeTime = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Recently" : formatDistanceToNow(date, { addSuffix: true });
};

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState([]);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [savingCategory, setSavingCategory] = useState("");
  const [emailSupported, setEmailSupported] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadNotifications = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError("");
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE), status });
    if (category) params.set("category", category);
    if (query) params.set("search", query);
    try {
      const result = await apiRequest(`/api/notifications?${params.toString()}`);
      const resultPageCount = Math.max(1, Math.ceil((result.total || 0) / PAGE_SIZE));
      if (page > resultPageCount) {
        setPage(resultPageCount);
        setTotal(result.total || 0);
        setUnreadCount(result.unreadCount || 0);
        return;
      }
      setNotifications(result.notifications || []);
      setCategories(result.categories || []);
      setTotal(result.total || 0);
      setUnreadCount(result.unreadCount || 0);
    } catch (requestError) {
      setError(requestError.message || "Unable to load notifications.");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [category, page, query, status]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    const refresh = () => loadNotifications(false);
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [loadNotifications]);

  const loadPreferences = async () => {
    setPreferencesLoading(true);
    try {
      const result = await apiRequest("/api/notifications/preferences");
      setPreferences(result.categories || []);
      setEmailSupported(Boolean(result.emailSupported));
    } catch (requestError) {
      setError(requestError.message || "Unable to load notification preferences.");
    } finally {
      setPreferencesLoading(false);
    }
  };

  useEffect(() => {
    loadPreferences();
  }, []);

  const refreshAfterAction = async () => {
    await loadNotifications(false);
    window.dispatchEvent(new Event("notifications:refresh"));
  };

  const setRead = async (notification, read) => {
    setError("");
    setMessage("");
    try {
      await apiRequest(`/api/notifications/${notification.id}/read`, {
        method: "PATCH",
        body: JSON.stringify({ read })
      });
      await refreshAfterAction();
    } catch (requestError) {
      setError(requestError.message || "Unable to update this notification.");
    }
  };

  const openNotification = async (notification) => {
    if (notification.readAt == null) await setRead(notification, true);
    if (notification.link && notification.link.startsWith("/") && !notification.link.startsWith("//")) {
      navigate(notification.link);
    }
  };

  const deleteNotification = async (notification) => {
    setError("");
    setMessage("");
    try {
      await apiRequest(`/api/notifications/${notification.id}`, { method: "DELETE" });
      setMessage("Notification deleted.");
      await refreshAfterAction();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete this notification.");
    }
  };

  const markAllRead = async () => {
    setError("");
    setMessage("");
    try {
      await apiRequest("/api/notifications/read-all", { method: "POST" });
      setMessage("All notifications marked as read.");
      await refreshAfterAction();
    } catch (requestError) {
      setError(requestError.message || "Unable to mark notifications as read.");
    }
  };

  const clearRead = async () => {
    if (!window.confirm("Delete all read notifications? This can't be undone.")) return;
    setError("");
    setMessage("");
    try {
      const result = await apiRequest("/api/notifications/read", { method: "DELETE" });
      setMessage(`${result.deleted || 0} read notifications cleared.`);
      await refreshAfterAction();
    } catch (requestError) {
      setError(requestError.message || "Unable to clear read notifications.");
    }
  };

  const toggleInApp = async (item) => {
    setSavingCategory(item.category);
    setError("");
    try {
      const saved = await apiRequest("/api/notifications/preferences", {
        method: "PUT",
        body: JSON.stringify({
          category: item.category,
          inAppEnabled: !item.inAppEnabled,
          emailEnabled: false
        })
      });
      setPreferences((items) => items.map((preference) => preference.category === item.category
        ? { ...preference, inAppEnabled: saved.inAppEnabled }
        : preference));
    } catch (requestError) {
      setError(requestError.message || "Unable to save this preference.");
    } finally {
      setSavingCategory("");
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const headingText = useMemo(() => {
    if (status === "unread") return "Unread notifications";
    if (status === "read") return "Read notifications";
    return "Your notifications";
  }, [status]);

  return (
    <section className="notifications-page" aria-labelledby="notifications-title">
      <div className="notifications-heading">
        <div>
          <p className="notifications-eyebrow">YOUR WORKSPACE</p>
          <h1 id="notifications-title">Notifications</h1>
          <p className="notifications-intro">Important updates and activity from across your workspace.</p>
        </div>
        <div className="notifications-heading-actions">
          <span className="notifications-unread-summary">
            <i className="ti ti-bell" aria-hidden="true" />
            {unreadCount} unread
          </span>
          <button className="notifications-secondary-button" type="button" onClick={() => setPreferencesOpen((open) => !open)} aria-expanded={preferencesOpen}>
            <i className="ti ti-adjustments-horizontal" aria-hidden="true" />
            Preferences
          </button>
        </div>
      </div>

      {preferencesOpen && (
        <section className="notifications-preferences" aria-labelledby="notification-preferences-title">
          <div className="notifications-preferences-heading">
            <div>
              <h2 id="notification-preferences-title">Notification preferences</h2>
              <p>Choose which categories can send in-app notifications.</p>
            </div>
            <button type="button" className="notifications-icon-button" aria-label="Close preferences" onClick={() => setPreferencesOpen(false)}>
              <i className="ti ti-x" aria-hidden="true" />
            </button>
          </div>
          {preferencesLoading ? (
            <PreferencesSkeleton />
          ) : (
            <>
              <div className="notifications-preference-row notifications-preference-labels" aria-hidden="true">
                <span>Category</span><span>In-app</span><span>Email</span>
              </div>
              {preferences.map((item) => (
                <div className="notifications-preference-row" key={item.category}>
                  <span>{item.category}</span>
                  <label className="notifications-switch">
                    <input
                      type="checkbox"
                      checked={item.inAppEnabled}
                      disabled={savingCategory === item.category}
                      onChange={() => toggleInApp(item)}
                      aria-label={`In-app notifications for ${item.category}`}
                    />
                    <span aria-hidden="true" />
                  </label>
                  <label className="notifications-switch notifications-email-switch" title={emailSupported ? "Email notifications" : "Email notifications aren't configured yet"}>
                    <input
                      type="checkbox"
                      checked={Boolean(item.emailEnabled)}
                      disabled={!emailSupported}
                      readOnly
                      aria-label={`Email notifications for ${item.category}${emailSupported ? "" : " (not configured)"}`}
                    />
                    <span aria-hidden="true" />
                  </label>
                </div>
              ))}
              {!emailSupported && <p className="notifications-email-note">Email alerts aren’t configured for this workspace yet.</p>}
            </>
          )}
        </section>
      )}

      <div className="notifications-toolbar">
        <div className="notifications-filter-tabs" role="tablist" aria-label="Notification status">
          {filters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              role="tab"
              aria-selected={status === filter.value}
              className={status === filter.value ? "is-active" : ""}
              onClick={() => { setStatus(filter.value); setPage(1); }}
            >
              {filter.label}
              {filter.value === "unread" && unreadCount > 0 && <span className="notifications-tab-count">{unreadCount > 99 ? "99+" : unreadCount}</span>}
            </button>
          ))}
        </div>
        <div className="notifications-search-filters">
          <label className="notifications-search">
            <i className="ti ti-search" aria-hidden="true" />
            <span className="visually-hidden">Search notifications</span>
            <input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search notifications" />
            {search && <button type="button" aria-label="Clear search" onClick={() => setSearch("")}><i className="ti ti-x" aria-hidden="true" /></button>}
          </label>
          <label className="notifications-category-filter">
            <span className="visually-hidden">Filter by category</span>
            <select value={category} onChange={(event) => { setCategory(event.target.value); setPage(1); }}>
              <option value="">All categories</option>
              {categories.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="notifications-list-header">
        <div>
          <h2>{headingText}</h2>
          {!loading && <span>{rangeStart}–{rangeEnd} of {total}</span>}
        </div>
        <div className="notifications-list-actions">
          <button type="button" className="notifications-text-button" onClick={markAllRead} disabled={unreadCount === 0}>
            <i className="ti ti-checks" aria-hidden="true" /> Mark all as read
          </button>
          <button type="button" className="notifications-text-button is-danger" onClick={clearRead} disabled={total === 0}>
            <i className="ti ti-trash" aria-hidden="true" /> Clear read
          </button>
        </div>
      </div>

      {error && <ToastFeedback type="error" message={error} />}
      {message && <ToastFeedback type="success" message={message} />}

      {loading ? (
        <div className="notifications-skeleton-list" aria-label="Loading notifications" aria-busy="true">
          {Array.from({ length: 5 }, (_, index) => (
            <div className="notifications-skeleton" key={index}>
              <span className="notifications-skeleton-icon" />
              <span className="notifications-skeleton-copy"><i /><i /><i /></span>
            </div>
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="notifications-empty">
          <span className="notifications-empty-icon"><i className="ti ti-bell-off" aria-hidden="true" /></span>
          <h3>{query || category ? "No matching notifications" : status === "unread" ? "You’re all caught up" : "Nothing to see here yet"}</h3>
          <p>{query || category ? "Try changing your search or category filter." : "Updates from your workspace will appear here when they happen."}</p>
          {(query || category || status !== "all") && (
            <button type="button" className="notifications-secondary-button" onClick={() => { setSearch(""); setCategory(""); setStatus("all"); setPage(1); }}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="notifications-list" aria-live="polite">
          {notifications.map((notification) => {
            const unread = notification.readAt == null;
            return (
              <article key={notification.id} className={`notification-card${unread ? " is-unread" : ""}`}>
                <button className="notification-open" type="button" onClick={() => openNotification(notification)} aria-label={`${unread ? "Unread: " : ""}${notification.title}. ${notification.message}`}>
                  <span className={`notification-icon ${notification.icon || ""}`} aria-hidden="true"><i className={`ti ${notification.icon || "ti-bell"}`} /></span>
                  <span className="notification-content">
                    <span className="notification-title-row"><strong>{notification.title}</strong>{unread && <span className="notification-unread-dot" aria-label="Unread" />}</span>
                    <span className="notification-message">{notification.message}</span>
                    <span className="notification-meta"><span>{notification.category}</span><span aria-hidden="true">·</span><time dateTime={notification.createdAt} title={new Date(notification.createdAt).toLocaleString()}>{relativeTime(notification.createdAt)}</time></span>
                  </span>
                  <i className="ti ti-chevron-right notification-chevron" aria-hidden="true" />
                </button>
                <div className="notification-actions">
                  <button type="button" className="notifications-icon-button" onClick={() => setRead(notification, unread)} aria-label={unread ? "Mark as read" : "Mark as unread"} title={unread ? "Mark as read" : "Mark as unread"}>
                    <i className={`ti ${unread ? "ti-mail-opened" : "ti-mail"}`} aria-hidden="true" />
                  </button>
                  <button type="button" className="notifications-icon-button is-danger" onClick={() => deleteNotification(notification)} aria-label="Delete notification" title="Delete notification">
                    <i className="ti ti-trash" aria-hidden="true" />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {!loading && pageCount > 1 && (
        <nav className="notifications-pagination" aria-label="Notification pages">
          <span>Page {page} of {pageCount}</span>
          <div>
            <button type="button" className="notifications-secondary-button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1}>
              <i className="ti ti-chevron-left" aria-hidden="true" /> Previous
            </button>
            <button type="button" className="notifications-secondary-button" onClick={() => setPage((value) => Math.min(pageCount, value + 1))} disabled={page >= pageCount}>
              Next <i className="ti ti-chevron-right" aria-hidden="true" />
            </button>
          </div>
        </nav>
      )}
    </section>
  );
};

export default Notifications;
